import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { surveysApi } from '@/api/surveys';
import DynamicSurveyForm from '@/components/form/DynamicSurveyForm';
import AnswerLinkInvalid from '@/components/tests/AnswerLinkInvalid';
import AnswerSubmitted from '@/components/tests/AnswerSubmitted';
import TestPageLayout from '@/components/tests/TestPageLayout';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';

export default function AnswerSurvey() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [progress, setProgress] = useState(0);
    const [submitted, setSubmitted] = useState(false);

    const {
        data: survey,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['survey-answer', token],
        queryFn: () => surveysApi.getSurveyByToken(token!),
        enabled: !!token,
        retry: false,
        staleTime: Infinity,
    });

    const submitMutation = useMutation({
        mutationFn: (data: { answers: Record<string, unknown>; status: string }) =>
            surveysApi.submitSurveyAnswer(token!, data),
        onSuccess: () => {
            setSubmitted(true);
            toast.success('Formulário enviado com sucesso!');
        },
        onError: () => {
            toast.error('Erro ao enviar formulário. Tente novamente.');
        },
    });

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
                <p>Token não fornecido.</p>
            </div>
        );
    }

    if (isLoading) return <Loading />;

    if (error) {
        return <AnswerLinkInvalid />;
    }

    if (submitted) {
        return <AnswerSubmitted />;
    }

    return (
        <TestPageLayout progress={progress}>
            <div className="rounded-xl bg-card p-6 shadow-elevation-1 duration-500 animate-in slide-in-from-bottom-4 md:p-8">
                <h1 className="mb-6 border-b pb-4 text-2xl font-bold text-foreground">
                    {survey?.name}
                </h1>
                {survey?.instructions ? (
                    <p className="mb-6 rounded-lg bg-brand-subtle p-4 text-sm leading-relaxed text-foreground">
                        {survey.instructions}
                    </p>
                ) : null}
                <DynamicSurveyForm
                    survey={survey!}
                    onSubmit={(values) =>
                        submitMutation.mutate({
                            answers: values,
                            status: 'completed',
                        })
                    }
                    onProgressChange={setProgress}
                    initialData={survey?.answers || {}}
                >
                    <div className="mt-8 flex justify-end">
                        <Button
                            type="submit"
                            size="lg"
                            className="w-full md:w-auto"
                            disabled={submitMutation.isPending}
                        >
                            {submitMutation.isPending ? 'Enviando...' : 'Enviar Respostas'}
                        </Button>
                    </div>
                </DynamicSurveyForm>
            </div>
        </TestPageLayout>
    );
}
